import { createAsyncThunk, createSlice, createAction } from "@reduxjs/toolkit";
import {
  clientMutation,
  clientQuery,
} from "../../../../../../../core/data/GraphQLApi";
import { endpoints } from "../../../../../../../core/defaultValues";
import { ExceptionManager } from "../../../../../../../core/logManager";
import moment from "moment";

import {
  resetALL,
  catalogLoad,
} from "../../../../../../../.globals/redux/actions";

// GraphQL query for searchClipRequests
const searchClipsQuery = `
    query searchClipRequests ($openSearch: String, $entities: [EntityInput]!, $hasNotes: Boolean, $hasStarred: Boolean, $isDeleted: Boolean, $dateRange: AWSDateTimeRange!, $limit: Int, $offset: Int, $status: String) {
      searchClipRequests (openSearch: $openSearch, entities: $entities, hasNotes: $hasNotes, hasStarred: $hasStarred, isDeleted: $isDeleted, dateRange: $dateRange, limit: $limit, offset: $offset, status: $status) {
          items {
              id
              accountId
              createdOn
              deviceId
              esn
              label
              status
              time
              tripID
              clipID
              rotate
              url
              startDate
              endDate
              notes
              starred
              errorMsg
              deviceModelId
              deviceModelName
              driverName
              driverExternalId
              thumbnailUrl
              transactionId
              details
              isDeleted
              isPrivate
              createdBy
              createdByUser {
                  authId
                  username
              }
          }
          total
      }
    }
`;

//STATE INITIAL
const INIT_STATE = {
  reponsehDeviceReadingVideo: {},
  reponsehClipRequestSmartwitness: {},
  items: [],
  total: 0,
  resetSearch: false,
  errorSearchClipRequests: false,
  openSearch: "",
  loadingSearchClipRequests: false,
  error: {},
  loadingClipRequestSmartwitness: false,
};

const route = "modules/video/views/main/.components/clips/redux/slice";

export const createClipRequestSmartwitness = createAsyncThunk(
  "deviceReadingVideo/createClipRequestSmartwitness",
  async (
    { date, deviceId, durationAfter, durationBefore, esn, mediaType }
  ) => {
    try {
      const result = await clientMutation(
        `mutation createSmartWitnessClipRequest ($date: AWSDateTime!, $deviceId: String, $durationBefore: Int, $durationAfter: Int, $esn: String!, $mediaType: String!) {
                    createSmartWitnessClipRequest (date: $date, deviceId: $deviceId, durationBefore: $durationBefore, durationAfter: $durationAfter, esn: $esn, mediaType: $mediaType) {
                        error
                        errorInfo {
                            Message
                        }
                        data {
                            id
                        }
                    }
                }`,
        {
          date: moment(date).toISOString(),
          deviceId: deviceId?.toString(),
          durationBefore,
          durationAfter,
          esn,
          mediaType,
        },
        endpoints.GRAPHQL_GENERAL_V2
      );

      const response = result?.createSmartWitnessClipRequest;
      if (!response || response.error)
        return { error: true, erroMessage: response?.errorInfo, data: null };

      return { error: false, data: response.data };
    } catch (exc) {
      return { error: true, erroMessage: { Message: exc?.error }, data: null };
    }
  }
);

export const searchClipRequests = createAsyncThunk(
  "clip/request/searchClipRequests",
  async (
    {
      limit,
      dateRange,
      resetSearch,
      offset,
      entities,
      hasStarred,
      hasNotes,
      isDeleted,
      status,
      openSearch,
    },
    { rejectWithValue }
  ) => {
    try {
      const variables = {
        dateRange,
        offset,
        limit,
        entities:
          !entities || !entities?.length || entities.find((e) => e.id == -1)
            ? [{ name: "All", id: -1 }]
            : entities,
        isDeleted: isDeleted || false,
        hasStarred: hasStarred || false,
        hasNotes: hasNotes || false,
        status: status || "all",
        openSearch,
      };

      const data = await clientQuery(
        searchClipsQuery,
        variables,
        endpoints.GRAPHQL_GENERAL
      );

      if (data) {
        const {
          searchClipRequests: { items, total },
        } = data;
        return { items, total, resetSearch, error: false, openSearch };
      } else {
        throw new Error("Error while searching clips video module");
      }
    } catch (exc) {
      ExceptionManager(
        exc,
        "modules/video/views/main/.components/clips/redux/slice",
        "searchClipRequests"
      );
      return rejectWithValue({ items: [], resetSearch: true, error: true });
    }
  }
);

//Slice
const clipRequestRedux = createSlice({
  name: "clip/request",
  initialState: INIT_STATE,
  reducers: {
    reset(state, action) {
      return action.payload
        ? { ...state, ...action.payload }
        : { ...INIT_STATE };
    },
    close(state, action) {
      state.openModal = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createClipRequestSmartwitness.pending, (state, action) => {
      state.loadingClipRequestSmartwitness = true;
    });
    builder.addCase(
      createClipRequestSmartwitness.fulfilled,
      (state, action) => {
        state.loadingClipRequestSmartwitness = false;
        state.reponsehClipRequestSmartwitness = action.payload;
      }
    );
    builder.addCase(createClipRequestSmartwitness.rejected, (state, action) => {
      state.loadingClipRequestSmartwitness = false;
      state.error = action.payload;
    });

    // searchClipRequests reducers
    builder.addCase(searchClipRequests.pending, (state, action) => {
      state.loadingSearchClipRequests = true;
    });
    builder.addCase(searchClipRequests.fulfilled, (state, action) => {
      state.loadingSearchClipRequests = false;
      state.items = action.payload.items;
      state.total = action.payload.total;
    });
    builder.addCase(searchClipRequests.rejected, (state, action) => {
      state.loadingSearchClipRequests = false;
      state.searchClipRequests = action.payload || {
        items: [],
        resetSearch: true,
        error: true,
      };
    });
  },
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = clipRequestRedux;

// Extract and export each action creator by name
export const { reset, close } = actions;

//Simple async action

// Export the reducer, either as a default or named export
export default reducer;
